import * as React from "react";
import {cx}       from "emotion";

import "./detail-rows.scss";

import {isApiTrue}       from "@Common/logic/is-api-true";
import {formatMoney2}    from "@Common/ui-components/language/format-money";
import {parseDateFormat} from "@Common/utils/date/parse-date-format";
import {chain}           from "@Common/utils/fs";

import {getDistributionLevelLabel} from "../../../checkout/product-cards/get-distribution-level-label";
import {formatServiceLevelName}    from "../../../checkout/shipping-section/common/service-level/format-service-level-name";

export const DetailRows = ({item, cart, showPricing}) => [
    ...AmountRows({showPricing}).map((row) => row(item)).filter(v=>v).flat().map((_) => (
        showPricing ? {
            columns: [
                {
                    render: () => _.label,
                },
                {
                    render: () => _.quantity,
                },
                {
                    render: () => formatMoney2(_.amount),
                },
            ],
        } : {
            columns: [
                {
                    render: () => _.label,
                },
                {
                    render: () => _.quantity,
                },
            ],
        }
    )),
    ...item.selections.distributionType === "shipping" ? (
        ShippingRows({item, cart, showPricing})
    ) : item.selections.distributionType === "mailing" ? (
        MailingRows({item, showPricing})
    ) : item.selections.distributionType === "pickup" && (
        PickupRows({})
    ) || [],
    (
        item.selections.distributionType !== "download" &&
        isApiTrue(item.selections.futureFulfillment)
    ) && {
        columns: [
            {
                colSpan: 3,
                render: () => fulfillmentMessage(item),
            },
        ],
    },
].filter(v=>v);

const MailingRows = ({item, showPricing}) => [
    {
        columns: showPricing ? [
            {
                render: () => (
                    `Addressed with: ` + item.selections.mailingListIds?.map((ml) => ml.name).join(", ")
                    + (item?.displaySampleAddress ? `, 1 Sample Card` : ``)
                ),
            },
            {
                render: () => (
                    item.selections.mailingListIds?.map((ml) => ml.quantity).reduce((t,v) => t+v, 0)
                    + (item?.displaySampleAddress? 1: 0)
                ),
            },
            {
                render: () => ((item.selections.distributionLevel !== '5' || (['mailing', 'mail'].includes(item.selections.distributionType)))? formatMoney2(item.selections.mailingListIds?.map((ml) => {
                    if(!['mailing', 'mail'].includes(item.selections.distributionType) && ml.unitPrice < 0) {
                        return 0;
                    }
                    return ml.quantity * +(ml.unitPrice??0);
                }).reduce((t,v) => t+v, 0)): ''),
            },
        ] : [
            {
                render: () => (
                    `Addressed with: ` + item.selections.mailingListIds?.map((ml) => ml.name).join(", ")
                    + (item?.displaySampleAddress? `, 1 Sample Card`: 0)
                ),
            },
            {
                render: () => (
                    item.selections.mailingListIds?.map((ml) => ml.quantity).reduce((t,v) => t+v, 0)
                    + (item?.displaySampleAddress? 1: 0)
                ),
            },
        ],
    },
    {
        columns: [
            {
                colSpan: 3,
                render: () => getDistributionLevelLabel(item),
            },
        ],
    },
];

const PickupRows = ({}) => [
    {
        columns: [
            {
                colSpan: 3,
                className: "detail-rows-458 shipping",
                render: () => <>
                    <div className="label">
                        Pickup Address
                    </div>

                    <div className="address">
                        {[
                            "Xpressdocs",
                            "1301 NE Loop 820",
                            "Fort Worth, TX 76131",
                            "+1 866-977-3627",
                        ].join("\n")}
                    </div>
                </>,
            },
        ],
    },
];

const ShippingRows = ({item, cart, showPricing}) => chain(
    item.shipping?.shippingAddress?.state ? item.shipping : cart.shipping,
    (shipping) => [
        {
            columns: showPricing ? [
                {
                    render: () => <>
                        <div className="line">
                            {formatServiceLevelName(shipping?.serviceLevelUpgraded? 'FedEx 2 Day': shipping.serviceLevel)}
                        </div>
                        {shipping?.serviceLevelUpgraded && (
                            <div className="line">
                                Shipping Service Level has been upgraded at no additional cost.
                            </div>
                        )}
                    </>,
                },
                {
                    colSpan: 2,
                    className: cx("detail-rows-458", {"free": +shipping.shippingCharge === 0}),
                    render: () => chain(
                        +shipping.shippingCharge,
                        (_) => !_ ? "Free Shipping!" : formatMoney2(_),
                    ),
                },
            ] : [
                {
                    colSpan: 2,
                    render: () => formatServiceLevelName(shipping.serviceLevel),
                },
            ],
        },
        {
            columns: [
                {
                    colSpan: 3,
                    className: "detail-rows-458 shipping",
                    render: () => <>
                        <div className="label">
                            Shipping Address
                        </div>

                        <div className="address">
                            {shipping.shippingAddress?.state ? chain(
                                shipping.shippingAddress,
                                (a) => [
                                    a?.contactName,
                                    a?.company,
                                    a?.address1,
                                    a?.address2,
                                    a?.city + ", " + a?.state + " " + a?.postalCode,
                                    a?.contactPhone,
                                ].filter(v => v).join("\n"),
                            ) : "N/A"}
                        </div>
                    </>,
                },
            ],
        },
        {
            columns: [
                {
                    colSpan: 3,
                    className: "summary",
                    render: () => <>
                        <div className="arrival">
                            Estimated Arrival:{" "}{item.shipping.estimatedDeliveryMessage}
                        </div>
                    </>,
                },
            ],
        },
    ],
);

const fulfillmentMessage = (item) => (
    "Fulfillment for this item is scheduled for " + parseDateFormat("mm/dd/yy").formatDate(
        parseDateFormat("yyyy-mm-dd").parseDate(item.selections.scheduledDate)
    )
);


const AmountRows = ({showPricing}) => [
    (item) => chain(
        item.selections.options.find((o) => o.dev_key === "paper_type"),
        (o) => o && ({
            label: !showPricing ? o.name : o.name + " - " + `(${formatMoney2(item.selections.isFlatRate ? item.selections.unitPrice / item.selections.quantity : item.selections.unitPrice)} each)`,
            quantity: item.selections.quantity,
            amount: item.selections.isFlatRate ?
                        item.selections.unitPrice :
                        item.selections.unitPrice * item.selections.quantity,
        }),
    ),
    (item) => chain(
        item.selections.options.find((o) => o.group === "Finishing"),
        (o) => o && ({
            label: (o.subGroup ?? o.name) + (showPricing ?  (" - " + `(${formatMoney2(+o.unitPrice)} each)`) : ""),
            quantity: item.selections.quantity,
            amount: +o.unitPrice * item.selections.quantity,
        }),
    ),
    (item) => chain(
        item.selections.options.find((o) => o.dev_key === "pdf_download"),
        (o) => o && o.value && ({
            label: "PDF Download",
            quantity: 1,
            amount: +o.flatPrice,
        }),
    ),

    (item) => chain(
        item.selections.options.filter((o) => o.group === "lineItem"),
        (lis) => lis.map((li) => ({
            label: li.name,
            quantity: li.dev_key === 'expedited_production' ? 1 :
                        item.selections.quantity,
            amount: (
                +(li.flatPrice??0) +
                +(li.unitPrice??0)
            ) * (li.dev_key === 'expedited_production' ? 1 : item.selections.quantity),
        })),
    ),

    (item) => chain(
        item.selections.options.filter((o) => o.group === "YARD SIGN OPTIONS"),
        (lis) => lis.map((li) => ({
            label: li.name,
            quantity: li.flatPrice ? null : item.selections.quantity,
            amount: li.flatPrice ?? li.unitPrice * item.selections.quantity,
        })),
    ),

    (item) => chain(
        item.itemTotals.find((charge) => charge.type === "handlingFee"),
        (c) => c && showPricing && {
            label: c.isFlatRate ? "Handling Fee" : "Handling Fee - " + `(${formatMoney2( c.amount/item.selections.quantity )} each)`,
            quantity: c.isFlatRate ? null : item.selections.quantity,
            amount: c.amount,
        }
    )
];
